import { Stack, Typography, Box } from "@mui/material";
import { useTBContext } from "../Contexts/useTBStates";

const SECTION_LABELS: Record<string, string> = {
    tournaments: 'Tournaments',
    groups: 'Collections',
    admin: 'Admin',
    userManagement: 'User Management',
    commandCenter: 'Command Center',
};

const PANEL_LABELS: Record<string, string> = {
    snapshot: 'Overview',
    eventList: 'Tournaments',
    groupList: 'Collections',
    imageLibrary: 'Admin Console',
    traditions: 'Traditions',
    userProfile: 'User Profile',
    rewards: 'Rewards',
    creditsDiscounts: 'Credits & Discounts',
    attestScores: 'Attest Scores',
    leaderboard: 'Leaderboard',
    challenges: 'Challenges',
    playoff: 'Scorecard Playoff',
    firstTee: 'First Tee',
    playerManagement: 'Player Management',
    paceOfPlay: 'Pace of Play',
    commandCenter: 'Command Center',
};

const CC_PANELS = [
    'attestScores',
    'leaderboard',
    'challenges',
    'playoff',
    'firstTee',
    'playerManagement',
    'paceOfPlay',
];

const EVENT_TAB_LABELS: Record<number, string> = {
    0: 'Event Details',
    1: 'Flighting',
    2: 'Comms & Info',
    3: 'Registrations',
    4: 'Round Details',
    5: 'Games',
    6: 'Tee Sheet',
};

const GROUP_TAB_LABELS: Record<number, string> = {
    0: 'Events',
    1: 'Players',
    2: 'Series Points',
    3: 'Standings',
    4: 'Collection Details',
};

interface CrumbItem {
    label: string;
    muted?: boolean;
    onClick?: () => void;
}

const Crumb = ({ label, muted, onClick }: CrumbItem) => (
    <Typography
        variant='caption'
        onClick={onClick}
        sx={{
            color: muted ? 'sepia.light' : 'sepia.main',
            fontWeight: muted ? 400 : 500,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            ...(onClick && {
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationColor: 'transparent',
                '&:hover': {
                    textDecorationColor: 'inherit',
                },
            }),
        }}
    >
        {label}
    </Typography>
);

const Separator = () => (
    <Typography variant='caption' sx={{ color: 'sepia.extraLight', mx: 0.5 }}>
        /
    </Typography>
);

export const NavigationHistoryBar = () => {
    const {
        appSection, setAppSection,
        viewPanel, setViewPanel,
        viewEvent, setViewEvent,
        eventTabValue, setEventTabValue,
        viewGroup, setViewGroup,
        groupTabValue, setGroupTabValue,
        factor,
        editMode,
    } = useTBContext();

    const scale = factor < 1 ? factor : 1;

    const crumbs: CrumbItem[] = [];

    const sectionLabel = SECTION_LABELS[appSection] ?? appSection;
    const panelLabel = PANEL_LABELS[viewPanel] ?? viewPanel;

    const goToCollection = (keepEvent: boolean) => {
        if (editMode) return;
        setAppSection('groups');
        setViewPanel('groupList');
        if (!keepEvent) {
            setViewEvent(null);
            setEventTabValue(0);
        }
        setGroupTabValue(0);
    };

    const goToTournamentEvent = (keepEvent: boolean) => {
        if (editMode) return;
        setAppSection('tournaments');
        setViewPanel('eventList');
        if (!keepEvent) {
            setViewEvent(null);
            setEventTabValue(0);
        }
    };

    if (appSection === 'tournaments') {
        if (viewEvent) {
            // "Tournaments" crumb — clickable, returns to event list
            crumbs.push({
                label: 'Tournaments',
                muted: false,
                onClick: editMode ? undefined : () => {
                    setViewEvent(null);
                    setEventTabValue(0);
                    setViewPanel('eventList');
                },
            });
            crumbs.push({ label: viewEvent.eventName ?? 'Event' });
            const tabLabel = EVENT_TAB_LABELS[eventTabValue];
            if (tabLabel) crumbs.push({ label: tabLabel, muted: true });
        } else {
            crumbs.push({ label: panelLabel, muted: true });
        }
    } else if (appSection === 'groups') {
        if (viewGroup && viewEvent) {
            // Nested: Collections / Trip Name / Event / Event Details
            crumbs.push({
                label: sectionLabel,
                muted: false,
                onClick: editMode ? undefined : () => {
                    setViewEvent(null);
                    setEventTabValue(0);
                    setViewGroup(null);
                },
            });
            crumbs.push({
                label: viewGroup.groupName ?? 'Collection',
                muted: false,
                onClick: editMode ? undefined : () => {
                    setViewEvent(null);
                    setEventTabValue(0);
                },
            });
            crumbs.push({ label: viewEvent.eventName ?? 'Event' });
            const tabLabel = EVENT_TAB_LABELS[eventTabValue];
            if (tabLabel) crumbs.push({ label: tabLabel, muted: true });
        } else if (viewGroup) {
            crumbs.push({
                label: sectionLabel,
                muted: false,
                onClick: editMode ? undefined : () => {
                    setViewGroup(null);
                },
            });
            crumbs.push({ label: viewGroup.groupName ?? 'Collection' });
            const tabLabel = GROUP_TAB_LABELS[groupTabValue];
            if (tabLabel) crumbs.push({ label: tabLabel, muted: true });
        } else {
            crumbs.push({ label: sectionLabel, muted: true });
        }
    } else if (appSection === 'admin') {
        // Each admin panel is its own top-level section in the new IA
        crumbs.push({ label: panelLabel, muted: true });
    } else if (appSection === 'commandCenter') {
        const ccPanelLabel = CC_PANELS.includes(viewPanel)
            ? (PANEL_LABELS[viewPanel] ?? viewPanel)
            : null;
        const ccHomeClick = editMode || viewPanel === 'attestScores' || !ccPanelLabel
            ? undefined
            : () => setViewPanel('attestScores');

        if (viewGroup && viewEvent) {
            // Collections / Trip / Event / Command Center [/ panel]
            crumbs.push({
                label: 'Collections',
                onClick: editMode ? undefined : () => {
                    setViewGroup(null);
                    goToCollection(false);
                },
            });
            crumbs.push({
                label: viewGroup.groupName ?? 'Collection',
                onClick: editMode ? undefined : () => goToCollection(false),
            });
            crumbs.push({
                label: viewEvent.eventName ?? 'Event',
                onClick: editMode ? undefined : () => goToCollection(true),
            });
            crumbs.push({ label: 'Command Center', onClick: ccHomeClick });
            if (ccPanelLabel) crumbs.push({ label: ccPanelLabel, muted: true });
        } else if (viewGroup) {
            // Collections / Trip / Command Center [/ panel]
            crumbs.push({
                label: 'Collections',
                onClick: editMode ? undefined : () => {
                    setViewGroup(null);
                    goToCollection(false);
                },
            });
            crumbs.push({
                label: viewGroup.groupName ?? 'Collection',
                onClick: editMode ? undefined : () => goToCollection(false),
            });
            crumbs.push({ label: 'Command Center', onClick: ccHomeClick });
            if (ccPanelLabel) crumbs.push({ label: ccPanelLabel, muted: true });
        } else if (viewEvent) {
            // Tournaments / Event / Command Center [/ panel]
            crumbs.push({
                label: 'Tournaments',
                onClick: editMode ? undefined : () => goToTournamentEvent(false),
            });
            crumbs.push({
                label: viewEvent.eventName ?? 'Event',
                onClick: editMode ? undefined : () => goToTournamentEvent(true),
            });
            crumbs.push({ label: 'Command Center', onClick: ccHomeClick });
            if (ccPanelLabel) crumbs.push({ label: ccPanelLabel, muted: true });
        } else if (ccPanelLabel) {
            crumbs.push({
                label: 'Command Center',
                onClick: ccHomeClick,
            });
            crumbs.push({ label: ccPanelLabel, muted: true });
        } else {
            crumbs.push({ label: 'Command Center', muted: true });
        }
    } else {
        crumbs.push({ label: sectionLabel, muted: true });
        if (panelLabel && panelLabel !== sectionLabel) {
            crumbs.push({ label: panelLabel, muted: false });
        }
    }

    return (
        <Box
            id='tournament-builder-nav-bar'
            sx={{
                height: 40 * scale,
                minHeight: 40 * scale,
                backgroundColor: 'sepia.background',
                borderBottom: '1px solid',
                borderColor: 'sepia.extraLight',
                px: 2,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
            }}
        >
            <Stack direction='row' alignItems='center'>
                {crumbs.map((crumb, i) => (
                    <Stack key={i} direction='row' alignItems='center'>
                        {i > 0 && <Separator />}
                        <Crumb label={crumb.label} muted={crumb.muted} onClick={crumb.onClick} />
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
};
